import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import {
  type IDropdownItem,
  Button,
  FullPageWrapper,
  IconButton,
  TextFieldWithLabel,
  Typography,
} from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { makeTextFieldAutoFocus } from 'shared/ui/TextField'
import { SelectFieldMultiList } from 'shared/ui/Select/SelectMultiListField/SelectMultiListField'
import { ChipWithAvatar } from 'entities/Phone/ui/AssignNumberCommon/ui/ChipWithAvatar/ChipWithAvatar'
import { MultiSelectDropdownContent } from 'entities/Phone/ui/AssignNumberCommon/ui/NewInboxContent/MultiSelectDropdownContent'
import { TeamsPath } from 'pages/settings/pages/teams/route/teamsPath'
import { useTeamsCreateContext } from 'pages/settings/pages/teams/ui/TeamsCreate/context/teamsCreateContext'

import styles from './styles.module.scss'

const NAME_EMPTY_ERROR = "This field can't be empty"

export const TeamsCreate = observer(() => {
  const store = useTeamsCreateContext()
  const [nameError, setNameError] = useState<string | undefined>(undefined)

  const handleSelectMember = (item: IDropdownItem) => {
    store.setSelectedMemberIds([...store.selectedMemberIds, Number(item.id)])
  }

  const handleDeleteMember = (item: IDropdownItem) => {
    store.setSelectedMemberIds(store.selectedMemberIds.filter((id) => id !== Number(item.id)))
  }

  const handleNameChange = (value: string) => {
    setNameError(undefined)
    store.setName(value)
  }

  const handleNameBlur = () => {
    if (store.name.trim() === '') {
      setNameError(NAME_EMPTY_ERROR)
    } else {
      setNameError(undefined)
    }
  }

  return (
    <FullPageWrapper className={styles.page}>
      <div className={styles.pageInner}>
        <IconButton
          className={styles.closeButton}
          ariaLabel='TeamsCreateClose'
          icon='close'
          size='medium'
          variant='icon'
          color='tertiary'
          onClick={() => uiStore.changeRoute({ path: TeamsPath.root })}
        />
        <div className={styles.content}>
          <div className={styles.titleWrap}>
            <Typography
              className={styles.title}
              ariaLabel='TeamsCreate_title'
              variant='body-xs-regular'
              tag='div'
            >
              New team
            </Typography>
            <Typography ariaLabel='TeamsCreate_subtitle' variant='heading-md'>
              Create team
            </Typography>
          </div>
          <div className={styles.section}>
            <TextFieldWithLabel
              labelProps={{
                label: {
                  text: 'Name',
                },
              }}
              errorMessage={nameError}
              textFieldProps={{
                value: store.name,
                onChange: handleNameChange,
                onBlur: handleNameBlur,
                InputProps: {
                  placeholder: 'Enter team name',
                },
                mainActions: [makeTextFieldAutoFocus({ withFocus: true })],
              }}
              limit={40}
            />
            <TextFieldWithLabel
              labelProps={{
                label: {
                  text: 'Description',
                  secondaryText: '(optional)',
                },
              }}
              textFieldProps={{
                value: store.description,
                onChange: store.setDescription,
                InputProps: {
                  placeholder: 'Enter team description',
                },
              }}
            />
            <SelectFieldMultiList
              ChipComponent={ChipWithAvatar}
              labelProps={{
                label: {
                  text: 'Members',
                },
              }}
              dropdownProps={{
                PopperProps: {
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: ({ reference }: { reference: { height: number } }) => {
                          return [0, -reference.height - 8]
                        },
                      },
                    },
                  ],
                },
              }}
              items={store.membersItems}
              selectedItems={store.selectedMembersItems}
              onDelete={handleDeleteMember}
              onSelect={handleSelectMember}
              fullWidth
              customComponent={() => (
                <MultiSelectDropdownContent
                  ChipComponent={ChipWithAvatar}
                  items={store.membersItems}
                  selectedItems={store.selectedMembersItems}
                  onSelect={handleSelectMember}
                  onDelete={handleDeleteMember}
                />
              )}
            />
            <Button
              text='Create'
              size='large'
              contained='primary'
              onClick={store.submit}
              disabled={!store.canSubmit}
              loading={store.submitting}
              aria-label={'TeamsCreateSubmit'}
              className={styles.submitButton}
            />
          </div>
        </div>
      </div>
    </FullPageWrapper>
  )
})
